import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { MdSave, MdRefresh } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { AsyncSearchableSelect, SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import {
    fetchAdminGpcCategories,
    fetchAdminGptCategories,
    fetchAdminGptMappings,
    fetchAdminIcecatMappings,
    fetchAdminPendingCategoryMappings,
    fetchAdminUnspscMappings,
    updateAdminGptMappings,
    updateAdminIcecatMappings,
    updateAdminPendingCategoryMappings,
    updateAdminUnspscMappings,
} from '../../../services/Http/AdminApi';

const TABS = {
    gpt: {
        key: 'gpt',
        label: 'GPT → GPC',
        fetch: fetchAdminGptMappings,
        update: updateAdminGptMappings,
        codeField: 'gpt_code',
        levelField: 'level',
        pathField: 'path',
        sourceLabel: 'GPT',
        supportsLevel: true,
        targetField: 'gpc',
        targetIdField: 'gpc_id',
        targetCodeField: 'gpc_code',
        targetFetch: fetchAdminGpcCategories,
        targetColumnLabel: 'AdminCategoryMapper.columns.gpc',
        targetPlaceholderKey: 'AdminCategoryMapper.placeholders.searchGpc',
    },
    unspsc: {
        key: 'unspsc',
        label: 'UNSPSC → GPC',
        fetch: fetchAdminUnspscMappings,
        update: updateAdminUnspscMappings,
        codeField: 'unspsc_code',
        levelField: 'level',
        sourceLabel: 'UNSPSC',
        supportsLevel: true,
        targetField: 'gpc',
        targetIdField: 'gpc_id',
        targetCodeField: 'gpc_code',
        targetFetch: fetchAdminGpcCategories,
        targetColumnLabel: 'AdminCategoryMapper.columns.gpc',
        targetPlaceholderKey: 'AdminCategoryMapper.placeholders.searchGpc',
    },
    icecat: {
        key: 'icecat',
        label: 'Icecat → GPC',
        fetch: fetchAdminIcecatMappings,
        update: updateAdminIcecatMappings,
        codeField: 'icecat_id',
        sourceLabel: 'Icecat',
        supportsLevel: false,
        targetField: 'gpc',
        targetIdField: 'gpc_id',
        targetCodeField: 'gpc_code',
        targetFetch: fetchAdminGpcCategories,
        targetColumnLabel: 'AdminCategoryMapper.columns.gpc',
        targetPlaceholderKey: 'AdminCategoryMapper.placeholders.searchGpc',
    },
    pending: {
        key: 'pending',
        label: 'Pending → GPT',
        fetch: fetchAdminPendingCategoryMappings,
        update: updateAdminPendingCategoryMappings,
        codeField: 'external_id',
        sourceLabel: 'Pending Category',
        supportsLevel: false,
        targetField: 'gpt',
        targetIdField: 'gpt_id',
        targetCodeField: 'gpt_code',
        targetFetch: fetchAdminGptCategories,
        targetColumnLabel: 'AdminCategoryMapper.columns.gpt',
        targetPlaceholderKey: 'AdminCategoryMapper.placeholders.searchGpt',
        showSourceMeta: true,
    },
};

const AdminCategoryMapper = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(TABS.gpt.key);
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [levelFilter, setLevelFilter] = useState('');
    const [unmappedOnly, setUnmappedOnly] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalRows, setTotalRows] = useState(0);

    const [pending, setPending] = useState({});
    const taxonomySearchDebounceRef = useRef(null);
    const taxonomySearchRequestSeqRef = useRef(0);

    useEffect(() => {
        loadMappings();
    }, [activeTab, currentPage, perPage, search, levelFilter, unmappedOnly]);

    useEffect(() => () => {
        if (taxonomySearchDebounceRef.current) {
            clearTimeout(taxonomySearchDebounceRef.current);
        }
    }, []);

    const loadMappings = async () => {
        const tab = TABS[activeTab];
        if (!tab) {
            return;
        }

        try {
            setLoading(true);
            const resp = await tab.fetch({
                per_page: perPage,
                page: currentPage,
                search,
                level: tab.supportsLevel ? (levelFilter || undefined) : undefined,
                unmapped: unmappedOnly ? 1 : 0,
            });
            const data = resp?.data || resp || [];
            setMappings(data);
            setTotalRows(resp?.meta?.total ?? data.length);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const setPendingField = (id, patch) => {
        setPending((prev) => ({
            ...prev,
            [id]: {
                ...(prev[id] || {}),
                ...patch,
            },
        }));
    };

    const saveMappings = async (ids = null) => {
        const tab = TABS[activeTab];
        if (!tab) {
            return;
        }

        const entries = Object.entries(pending)
            .filter(([id]) => !ids || ids.includes(Number(id)))
            .map(([id, changes]) => ({ source_id: Number(id), ...changes }));

        if (!entries.length) {
            return;
        }

        try {
            await tab.update({ mappings: entries });
            setPending((prev) => {
                const next = { ...prev };
                entries.forEach((entry) => {
                    delete next[entry.source_id];
                });
                return next;
            });
            await loadMappings();
        } catch (err) {
            setError(err);
        }
    };

    const debouncedTargetLoadOptions = useMemo(() => {
        const tab = TABS[activeTab];
        return (inputValue) => new Promise((resolve) => {
            if (taxonomySearchDebounceRef.current) {
                clearTimeout(taxonomySearchDebounceRef.current);
            }

            const requestSeq = ++taxonomySearchRequestSeqRef.current;

            taxonomySearchDebounceRef.current = setTimeout(async () => {
                try {
                    const result = await loadTaxonomyOptions(inputValue, tab.targetFetch, tab.targetCodeField);
                    // Ignore stale async completions if user kept typing.
                    if (requestSeq !== taxonomySearchRequestSeqRef.current) {
                        return;
                    }
                    resolve(result);
                } catch (_) {
                    resolve([]);
                }
            }, 350);
        });
    }, [activeTab]);

    const columns = useMemo(() => [
        {
            name: TABS[activeTab]?.sourceLabel || 'Source',
            cell: (row) => (
                <GpcCell>
                    <strong>{row.name}</strong>
                    <span>{t('AdminCategoryMapper.codeLabel')}: {row[TABS[activeTab]?.codeField] ?? '-'}</span>
                    {TABS[activeTab]?.supportsLevel && <span>{t('AdminCategoryMapper.levelLabel')}: {row.level ?? '-'}</span>}
                    <small>{row[TABS[activeTab]?.pathField] || ''}</small>
                    {TABS[activeTab]?.showSourceMeta && (
                        <>
                            <small>{row.organization_name ? `Org: ${row.organization_name}` : ''}</small>
                            <small>{row.import_id ? `Import: ${row.import_id}` : ''}</small>
                        </>
                    )}
                </GpcCell>
            ),
        },
        {
            name: t(
                TABS[activeTab]?.targetColumnLabel || 'AdminCategoryMapper.columns.gpc',
                { defaultValue: TABS[activeTab]?.targetField?.toUpperCase() || 'GPC' },
            ),
            cell: (row) => (
                <AsyncSearchableSelect
                    cacheOptions
                    defaultOptions={false}
                    loadOptions={debouncedTargetLoadOptions}
                    value={getSelectedOption(
                        row,
                        TABS[activeTab]?.targetField,
                        TABS[activeTab]?.targetIdField,
                        TABS[activeTab]?.targetCodeField,
                        pending,
                    )}
                    onChange={(_, __, option) => setPendingField(row.id, {
                        [TABS[activeTab]?.targetIdField]: option ? option.value : null,
                    })}
                    isClearable
                    placeholder={t(
                        TABS[activeTab]?.targetPlaceholderKey || 'AdminCategoryMapper.placeholders.searchGpc',
                        { defaultValue: `Search ${TABS[activeTab]?.targetField?.toUpperCase() || 'GPC'}` },
                    )}
                    styles={selectStyles}
                />
            ),
        },
        {
            name: t('Common.actions'),
            cell: (row) => (
                <InlineActions>
                    <IconButton
                        type="button"
                        onClick={() => saveMappings([row.id])}
                        disabled={!pending[row.id]}
                        title={t('AdminCategoryMapper.actions.saveRow')}
                    >
                        <MdSave size={18} />
                    </IconButton>
                </InlineActions>
            ),
            ignoreRowClick: true,
        },
    ], [pending, activeTab, t, debouncedTargetLoadOptions]);

    if (loading) {
        return <LoadingMessage>{t('AdminCategoryMapper.loading')}</LoadingMessage>;
    }

    if (error) {
        return <ErrorMessage>{error.message || t('AdminCategoryMapper.loadError')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('AdminCategoryMapper.pageTitle')}</PageTitle>
            </PageHeader>
            <PageContent>
                <Tabs>
                    {Object.values(TABS).map((tab) => (
                        <TabButton
                            key={tab.key}
                            type="button"
                            $active={activeTab === tab.key}
                            onClick={() => {
                                setActiveTab(tab.key);
                                setCurrentPage(1);
                                setPending({});
                                setError(null);
                            }}
                        >
                            {t(`AdminCategoryMapper.tabs.${tab.key}`, { defaultValue: tab.label })}
                        </TabButton>
                    ))}
                </Tabs>
                <Controls>
                    <ControlGroup>
                        <label>{t('AdminCategoryMapper.searchSourceLabel', { source: TABS[activeTab]?.sourceLabel })}</label>
                        <input value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} />
                    </ControlGroup>
                    {TABS[activeTab]?.supportsLevel && (
                        <ControlGroup>
                            <label>{t('AdminCategoryMapper.filters.level')}</label>
                            <SimpleSelect
                                value={levelFilter}
                                onChange={(event, nextValue) => {
                                    setLevelFilter(nextValue ?? event?.target?.value ?? '');
                                    setCurrentPage(1);
                                }}
                                options={[
                                    { value: '', label: t('Common.all') },
                                    ...[1, 2, 3, 4, 5, 6].map((lvl) => ({ value: lvl, label: String(lvl) })),
                                ]}
                                inputId="admin-category-mapper-level"
                            />
                        </ControlGroup>
                    )}
                    <ControlGroup>
                        <label>{t('AdminCategoryMapper.filters.unmappedOnly')}</label>
                        <input
                            type="checkbox"
                            checked={unmappedOnly}
                            onChange={(e) => { setUnmappedOnly(e.target.checked); setCurrentPage(1); }}
                        />
                    </ControlGroup>
                    <ControlGroup>
                        <label>{t('Common.rows')}</label>
                        <SimpleSelect
                            value={perPage}
                            onChange={(event, nextValue) => {
                                setPerPage(Number(nextValue ?? event?.target?.value ?? 25));
                                setCurrentPage(1);
                            }}
                            options={[25, 50, 100].map((count) => ({ value: count, label: String(count) }))}
                            inputId="admin-category-mapper-rows"
                        />
                    </ControlGroup>
                    <PrimaryButton type="button" onClick={() => saveMappings()} disabled={!Object.keys(pending).length}>
                        <MdSave size={18} /> {t('AdminCategoryMapper.actions.saveAll', { count: Object.keys(pending).length })}
                    </PrimaryButton>
                    <SecondaryButton type="button" onClick={loadMappings}>
                        <MdRefresh size={18} /> {t('Common.refresh')}
                    </SecondaryButton>
                </Controls>

                <DataTable
                    columns={columns}
                    data={mappings}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    onChangePage={setCurrentPage}
                    onChangeRowsPerPage={(newPerPage, page) => {
                        setPerPage(newPerPage);
                        setCurrentPage(page);
                    }}
                    dense
                />
            </PageContent>
        </PageContainer>
    );
};

const buildOption = (item, codeField) => {
    const code = item[codeField] ? `${item[codeField]} - ` : '';
    const path = item.path ? ` • ${item.path}` : '';
    return {
        value: item.id,
        label: `${code}${item.name}${path}`,
        data: item,
    };
};

const getSelectedOption = (row, fieldName, idField, codeField, pending) => {
    const pendingId = pending[row.id]?.[idField];
    if (pendingId === null) {
        return null;
    }

    if (pendingId && row[fieldName] && row[fieldName].id === pendingId) {
        return buildOption(row[fieldName], codeField);
    }

    if (!pendingId && row[fieldName]) {
        return buildOption(row[fieldName], codeField);
    }

    if (pendingId) {
        return { value: pendingId, label: `ID ${pendingId}` };
    }

    return null;
};

const loadTaxonomyOptions = async (inputValue, fetchFn, codeField) => {
    const query = inputValue.trim();
    if (query.length < 2) {
        return [];
    }

    const resp = await fetchFn({ search: query, per_page: 50 });
    const items = resp?.data || resp || [];
    return items.map((item) => buildOption(item, codeField));
};

export default AdminCategoryMapper;

const Tabs = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
`;

const TabButton = styled.button`
    border: 1px solid ${({ theme, $active }) => ($active ? 'var(--color-primary)' : theme.colors.border)};
    background: ${({ theme, $active }) => ($active ? 'var(--color-primary)' : theme.colors.surface)};
    color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.textPrimary)};
    border-radius: 999px;
    padding: 0.4rem 0.9rem;
    cursor: pointer;
    font-weight: 600;
`;

const Controls = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: flex-end;
    margin-bottom: 1rem;
`;

const ControlGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.35rem;

    label {
        font-size: 0.875rem;
        color: ${({ theme }) => theme.colors.textMuted};
    }

    input, select {
        padding: 0.45rem 0.6rem;
        border: 1px solid ${({ theme }) => theme.colors.border};
        border-radius: 6px;
        background: ${({ theme }) => theme.colors.surface};
        color: ${({ theme }) => theme.colors.textPrimary};
    }
`;

const selectStyles = {
    control: (base) => ({
        ...base,
        minWidth: 220,
        borderColor: 'var(--color-primary-bg-soft)',
        boxShadow: 'none',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 20,
    }),
};

const GpcCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.2rem;

    span {
        font-size: 0.8125rem;
        color: ${({ theme }) => theme.colors.textMuted};
    }

    small {
        color: ${({ theme }) => theme.colors.textMuted};
        opacity: 0.8;
    }
`;

const TranslationGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(120px, 1fr));
    gap: 0.5rem;
`;

const TranslationField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    label {
        font-size: 0.6875rem;
        color: ${({ theme }) => theme.colors.textMuted};
        text-transform: uppercase;
    }

    input {
        padding: 0.35rem 0.5rem;
        border: 1px solid ${({ theme }) => theme.colors.border};
        border-radius: 6px;
        background: ${({ theme }) => theme.colors.surface};
        color: ${({ theme }) => theme.colors.textPrimary};
    }
`;

const InlineActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.35rem;
`;

const IconButton = styled.button`
    background: ${({ theme }) => theme.colors.surfaceAlt};
    border: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-radius: 6px;
    padding: 0.35rem 0.45rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: var(--color-primary);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 0.55rem 0.9rem;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.7;
    }
`;

const SecondaryButton = styled(PrimaryButton)`
    background: var(--color-primary-bg-soft);
    color: var(--color-primary);
`;
